import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, OutlinedInput, Select, Stack, TextField, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  CampaignDoc,
  ContactList,
  FollowUpAudience,
  FollowUpDelayUnit,
  TemplateVariableMapping,
  addCampaignFollowUp,
  cancelCampaign,
  deleteCampaign,
  duplicateCampaign,
  getLists,
  pauseCampaign,
  prepareCampaign,
  rescheduleCampaign,
  resumeCampaign,
  startCampaign,
  testSendCampaign,
  updateCampaign,
  waitForCampaignReady,
} from "../../services/campaign.service";
import { WhatsAppTemplate } from "../../models/whatsapp.model";
import { getAllTemplates } from "../../services/settings.service";
import useAuthUser from "../hooks/useAuthUser";
import TemplateVariableMapper, { componentsFromVariableMappings, mappingsFromTemplateConfig } from "./TemplateVariableMapper";

type Props = {
  uuid: string;
  campaign: CampaignDoc;
  onRefresh: () => Promise<unknown> | void;
  onBack: () => void;
  onOpenCampaign: (id: string) => void;
};

const toLocalInput = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 16);
};

export default function CampaignManagementPanel({ uuid, campaign, onRefresh, onBack, onOpenCampaign }: Props) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [name, setName] = useState(campaign.name);
  const [scheduledLocal, setScheduledLocal] = useState(toLocalInput(campaign.scheduledAt));
  const [delayAmount, setDelayAmount] = useState(2);
  const [delayUnit, setDelayUnit] = useState<FollowUpDelayUnit>("DAYS");
  const [audience, setAudience] = useState<FollowUpAudience>("ALL");
  const [templateName, setTemplateName] = useState(campaign.template?.name || "");
  const [language, setLanguage] = useState(campaign.template?.language || "en_US");
  const [followUpVariableMappings, setFollowUpVariableMappings] = useState<TemplateVariableMapping[]>([]);
  const [campaignTemplateName, setCampaignTemplateName] = useState(campaign.template?.name || "");
  const [campaignLanguage, setCampaignLanguage] = useState(campaign.template?.language || "en_US");
  const [campaignVariableMappings, setCampaignVariableMappings] = useState<TemplateVariableMapping[]>(mappingsFromTemplateConfig(campaign.template));
  const [testNumber, setTestNumber] = useState("");
  const [lists, setLists] = useState<ContactList[]>([]);
  const [approvedTemplates, setApprovedTemplates] = useState<WhatsAppTemplate[]>([]);
  const [campaignListIds, setCampaignListIds] = useState<string[]>(campaign.listIds || []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  useEffect(() => {
    setName(campaign.name);
    setScheduledLocal(toLocalInput(campaign.scheduledAt));
    setCampaignTemplateName(campaign.template?.name || "");
    setCampaignLanguage(campaign.template?.language || "en_US");
    setCampaignVariableMappings(mappingsFromTemplateConfig(campaign.template));
    setCampaignListIds(campaign.listIds || []);
  }, [campaign.name, campaign.scheduledAt, campaign.template, campaign.listIds]);

  useEffect(() => {
    getLists(uuid).then(setLists).catch(() => setLists([]));
  }, [uuid]);

  const { user } = useAuthUser();
  useEffect(() => {
    if (!user?.uid) return;
    getAllTemplates(user.uid)
      .then((templates) => setApprovedTemplates((templates || []).filter((template: any) => template.status === "APPROVED")))
      .catch(() => setApprovedTemplates([]));
  }, [user?.uid]);

  const availableColumns = useMemo(
    () => Array.from(new Set(["name", "phone", ...lists.filter((list) => campaignListIds.includes(list._id)).flatMap((list) => list.columns || [])])),
    [lists, campaignListIds],
  );
  const selectedCampaignTemplate = approvedTemplates.find((template) => template.name === campaignTemplateName) || null;
  const selectedFollowUpTemplate = approvedTemplates.find((template) => template.name === templateName) || null;
  const mappingsIncomplete = (mappings: TemplateVariableMapping[]) => mappings.some(
    (mapping) => (mapping.source === "COLUMN" && !mapping.column) || (mapping.source === "STATIC" && !mapping.value?.trim()),
  );

  const status = String(campaign.status || "").toUpperCase();
  const editable = ["DRAFT", "READY", "SCHEDULED"].includes(status);

  const run = async (operation: () => Promise<any>, success: string, confirmation?: string) => {
    if (confirmation && !window.confirm(confirmation)) return;
    setBusy(true);
    setError("");
    setInfo("");
    try {
      await operation();
      setInfo(success);
      await onRefresh();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Campaign action failed");
    } finally {
      setBusy(false);
    }
  };

  const addFollowUp = () => {
    return run(
      () => addCampaignFollowUp(uuid, campaign._id, {
        delayAmount,
        delayUnit,
        audience,
        template: { name: templateName.trim(), language: language.trim() || "en_US", components: componentsFromVariableMappings(followUpVariableMappings), variableMappings: followUpVariableMappings },
      }),
      "Follow-up added.",
    );
  };

  const removeCampaign = async () => {
    if (!window.confirm("Permanently delete this campaign? This is only allowed when no messages were sent.")) return;
    setBusy(true);
    setError("");
    try {
      await deleteCampaign(uuid, campaign._id);
      onBack();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Could not delete campaign");
      setBusy(false);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="outlined" startIcon={<SettingsOutlinedIcon />} onClick={() => setSettingsOpen(true)}>
          Campaign settings
        </Button>
      </Box>
      <Dialog open={settingsOpen} onClose={() => !busy && setSettingsOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle fontWeight={800}>Campaign settings</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        {info && <Alert severity="success">{info}</Alert>}

        <Stack direction={{ xs: "column", md: "row" }} spacing={1} flexWrap="wrap" useFlexGap>
          {status === "DRAFT" && <Button variant="contained" disabled={busy} onClick={() => run(async () => { await prepareCampaign(uuid, campaign._id); await waitForCampaignReady(uuid, campaign._id); }, "Recipients prepared.")}>Prepare recipients</Button>}
          {status === "READY" && <Button variant="contained" startIcon={<PlayArrowIcon />} disabled={busy} onClick={() => run(() => startCampaign(uuid, campaign._id), "Campaign started.")}>Start now</Button>}
          {["RUNNING", "SCHEDULED"].includes(status) && <Button startIcon={<PauseIcon />} disabled={busy} onClick={() => run(() => pauseCampaign(uuid, campaign._id), "Campaign paused.")}>Pause</Button>}
          {status === "PAUSED" && <Button startIcon={<PlayArrowIcon />} disabled={busy} onClick={() => run(() => resumeCampaign(uuid, campaign._id), "Campaign resumed.")}>Resume</Button>}
          {!['COMPLETED', 'CANCELLED'].includes(status) && <Button color="warning" disabled={busy} onClick={() => run(() => cancelCampaign(uuid, campaign._id), "Campaign cancelled.", "Cancel this campaign and all pending follow-ups?")}>Cancel</Button>}
          <Button startIcon={<ContentCopyIcon />} disabled={busy} onClick={async () => {
            setBusy(true); setError("");
            try { const result = await duplicateCampaign(uuid, campaign._id); onOpenCampaign(result.id); }
            catch (e: any) { setError(e?.response?.data?.detail || e?.message || "Could not duplicate campaign"); }
            finally { setBusy(false); }
          }}>Duplicate</Button>
          <Button color="error" startIcon={<DeleteOutlineIcon />} disabled={busy} onClick={removeCampaign}>Delete</Button>
        </Stack>

        {editable && (
          <>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
              <TextField label="Campaign name" value={name} onChange={(event) => setName(event.target.value)} fullWidth />
              <Button variant="outlined" disabled={busy || !name.trim() || name.trim() === campaign.name} onClick={() => run(() => updateCampaign(uuid, campaign._id, { name: name.trim() }), "Campaign updated.")}>Save name</Button>
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
              <FormControl fullWidth>
                <InputLabel>Recipient lists</InputLabel>
                <Select
                  multiple
                  value={campaignListIds}
                  onChange={(event) => setCampaignListIds(typeof event.target.value === "string" ? event.target.value.split(",") : event.target.value)}
                  input={<OutlinedInput label="Recipient lists" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((id) => <Chip key={id} size="small" label={lists.find((list) => list._id === id)?.name || id} />)}
                    </Box>
                  )}
                >
                  {lists.map((list) => <MenuItem key={list._id} value={list._id}>{list.name}</MenuItem>)}
                </Select>
              </FormControl>
              <Button variant="outlined" disabled={busy || !campaignListIds.length} onClick={() => run(() => updateCampaign(uuid, campaign._id, { listIds: campaignListIds }), "Recipient lists updated and preparation restarted.")}>Save audience</Button>
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
              <FormControl fullWidth>
                <InputLabel>Approved template</InputLabel>
                <Select value={campaignTemplateName} label="Approved template" onChange={(event) => {
                  setCampaignTemplateName(event.target.value);
                  const selected = approvedTemplates.find((template) => template.name === event.target.value);
                  if (selected?.language) setCampaignLanguage(selected.language);
                }}>
                  {approvedTemplates.map((template) => <MenuItem key={template.name} value={template.name}>{template.name}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Language" value={campaignLanguage} onChange={(event) => setCampaignLanguage(event.target.value)} fullWidth />
              <Button variant="outlined" disabled={busy || !campaignTemplateName.trim() || mappingsIncomplete(campaignVariableMappings)} onClick={() => {
                return run(() => updateCampaign(uuid, campaign._id, { template: { name: campaignTemplateName.trim(), language: campaignLanguage.trim() || "en_US", components: componentsFromVariableMappings(campaignVariableMappings), variableMappings: campaignVariableMappings } }), "Template updated.");
              }}>Save template</Button>
            </Stack>
            <TemplateVariableMapper template={selectedCampaignTemplate} availableColumns={availableColumns} value={campaignVariableMappings} onChange={setCampaignVariableMappings} title="Campaign template variables" />
            <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
              <TextField type="datetime-local" label="Campaign schedule" value={scheduledLocal} onChange={(event) => setScheduledLocal(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
              <Button variant="outlined" disabled={busy || !scheduledLocal} onClick={() => run(() => rescheduleCampaign(uuid, campaign._id, new Date(scheduledLocal).toISOString()), "Campaign rescheduled.")}>Save schedule</Button>
            </Stack>
          </>
        )}

        <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
          <TextField label="Test WhatsApp number" placeholder="+919999999999" value={testNumber} onChange={(event) => setTestNumber(event.target.value)} fullWidth />
          <Button variant="outlined" disabled={busy || !testNumber.trim()} onClick={() => run(() => testSendCampaign(uuid, campaign._id, testNumber), "Test message sent.")}>Send test</Button>
        </Stack>

        {(campaign.followUpJobs?.length || 0) < 5 && (
          <>
            <Typography variant="subtitle1" fontWeight={800}>Add follow-up child campaign</Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
              <FormControl fullWidth>
                <InputLabel>Approved follow-up template</InputLabel>
                <Select value={templateName} label="Approved follow-up template" onChange={(event) => {
                  setTemplateName(event.target.value);
                  const selected = approvedTemplates.find((template) => template.name === event.target.value);
                  if (selected?.language) setLanguage(selected.language);
                }}>
                  {approvedTemplates.map((template) => <MenuItem key={template.name} value={template.name}>{template.name}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Language" value={language} onChange={(event) => setLanguage(event.target.value)} fullWidth />
              <TextField type="number" label="Delay" value={delayAmount} onChange={(event) => setDelayAmount(Number(event.target.value))} fullWidth />
              <TextField select label="Unit" value={delayUnit} onChange={(event) => setDelayUnit(event.target.value as FollowUpDelayUnit)} fullWidth><MenuItem value="MINUTES">Minutes</MenuItem><MenuItem value="HOURS">Hours</MenuItem><MenuItem value="DAYS">Days</MenuItem></TextField>
              <TextField select label="Audience" value={audience} onChange={(event) => setAudience(event.target.value as FollowUpAudience)} fullWidth><MenuItem value="ALL">All</MenuItem><MenuItem value="INTERESTED">Interested</MenuItem></TextField>
            </Stack>
            <TemplateVariableMapper template={selectedFollowUpTemplate} availableColumns={availableColumns} value={followUpVariableMappings} onChange={setFollowUpVariableMappings} title="Follow-up template variables" />
            <Button variant="contained" disabled={busy || !templateName.trim() || delayAmount < 1 || mappingsIncomplete(followUpVariableMappings)} onClick={addFollowUp}>Add follow-up</Button>
          </>
        )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)} disabled={busy}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
